import React, { FC } from 'react';
import styled from 'styled-components';
import { Field, FieldProps } from 'formik';
import Icon from '@minecraft.icon';
import { BodyTextLarge, DetailTextLarge, FlexCol, FlexRow, FlexDynamic, FlexStyleProps } from '../../atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';

export interface RadioCardProps {
  label?: string;
  value: string;
  selected: boolean;
  bodyText?: string;
  icon?: string;
  badgeText?: string;
  onClick: (value: string) => void;
  disabled?: boolean;
  hideLabel?: boolean;
  error?: boolean;
}

export type RadioCardOption = Omit<RadioCardProps, 'onClick' | 'selected'>;

const StyledRadioCard = styled.div<{ $selected: boolean; $disabled: boolean; $error: boolean }>`
  display: flex;
  flex-direction: row;
  padding: 1rem;
  gap: 1rem;

  align-items: center;

  background-color: ${({ $disabled, theme }) =>
    $disabled ? theme.designSystem.bgColors.disabled : theme.designSystem.bgColors.card};

  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  border: ${({ $selected, $error }) => ($selected || $error ? '0.21rem' : '0.15rem')} solid
    ${({ $selected, $error, theme }) => {
      if ($error) return theme.designSystem.borderColors.danger;
      if ($selected) return theme.designSystem.borderColors.primary;
      return theme.designSystem.borderColors.default;
    }};
  border-radius: 0.5rem;

  &:active {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.designSystem.borderColors.default : theme.designSystem.borderColors.primary};
  }

  &:hover {
    background-color: ${({ theme, $disabled }) =>
      $disabled ? theme.designSystem.bgColors.disabled : theme.designSystem.bgColors.cardHover};
  }

  &:focus {
    outline: 0.22rem solid ${({ theme }) => theme.designSystem.boxShadows.cardFocus};

    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.designSystem.borderColors.default : theme.designSystem.borderColors.primary};
    border-width: ${({ $selected }) => ($selected ? '0.21rem' : '0.08rem')};

    background-color: ${({ theme, $disabled }) =>
      $disabled ? theme.designSystem.bgColors.disabled : theme.designSystem.bgColors.cardHover};
  }
`;

const getTextColor = (disabled: boolean, error: boolean) => {
  if (disabled) return 'disabled';
  if (error) return 'error';
  return 'default';
};

export const RadioCard: FC<RadioCardProps> = ({
  label,
  badgeText,
  bodyText,
  icon,
  onClick,
  selected,
  value,
  disabled = false,
  hideLabel = false,
  error = false,
}) => {
  return (
    <StyledRadioCard
      role="radio"
      aria-label={label || bodyText}
      $selected={selected}
      $disabled={disabled}
      $error={error}
      aria-checked={selected}
      aria-disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onClick(value);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') onClick(value);
      }}
      tabIndex={0}
    >
      {icon && <Icon name={icon} size="2xl" />}
      <FlexCol alignContent="start" gap="3">
        <FlexRow gap="3" hidden={hideLabel && !badgeText}>
          {!hideLabel && (
            <BodyTextLarge fontWeight="semibold" color={getTextColor(disabled, error)} as="span">
              {label}
            </BodyTextLarge>
          )}
          {badgeText && (
            <FlexCol bgColor="badge" borderRadius="full" className="cn_atom_px-4 cn_atom_py-1">
              <DetailTextLarge fontWeight="semibold" color="inverted" as="span">
                {badgeText}
              </DetailTextLarge>
            </FlexCol>
          )}
        </FlexRow>
        {bodyText && (
          <FlexRow w="4/5">
            <BodyTextLarge color={getTextColor(disabled, error)} as="span">
              {bodyText}
            </BodyTextLarge>
          </FlexRow>
        )}
      </FlexCol>
    </StyledRadioCard>
  );
};

export interface RadioCardGroupProps {
  label: string;
  value: string;
  options: RadioCardOption[];
  onChange: (value: string) => void;
  flexDirection?: FlexStyleProps['direction'];
  flexGap?: FlexStyleProps['gap'];
  error?: boolean;
}

export const RadioCardGroup: FC<RadioCardGroupProps> = ({
  label,
  onChange,
  options,
  value,
  flexDirection = 'row',
  flexGap,
  error = false,
}) => {
  if (!options.length) return null;

  return (
    <FlexDynamic aria-label={label} role="radiogroup" direction={flexDirection} gap={flexGap}>
      {options.map((option) => (
        <RadioCard
          key={option.label || option.bodyText}
          label={option.label}
          selected={value === option.value}
          bodyText={option.bodyText}
          badgeText={option.badgeText}
          icon={option.icon}
          onClick={onChange}
          value={option.value}
          disabled={option.disabled}
          hideLabel={option.hideLabel}
          error={error}
        />
      ))}
    </FlexDynamic>
  );
};

export interface WrappedRadioCardGroup {
  inputProps: RadioCardGroupProps;
  wrapperProps?: FieldWrapperProps;
}

export const WrappedRadioCardGroup: FC<WrappedRadioCardGroup> = ({ wrapperProps, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <RadioCardGroup {...inputProps} />
    </FieldWrapper>
  );
};

export type ControlledRadioCardGroupProps = Omit<RadioCardGroupProps, 'onChange' | 'value'>;

export interface ConnecetedRadioCardGroupProps {
  inputProps: ControlledRadioCardGroupProps;
  wrapperProps?: FieldWrapperProps;
  name: string;
}

export const ConnectedRadioCardGroup: FC<ConnecetedRadioCardGroupProps> = ({ inputProps, wrapperProps, name }) => {
  return (
    <Field name={name}>
      {({ field: { value: formikValue }, form: { errors, setFieldValue } }: FieldProps<string>) => {
        const hasError = errors?.[name] || inputProps.error;
        const wrapperValidation = hasError
          ? { validation: { valid: !hasError, message: errors?.[name]?.toString() } }
          : {};

        const onChange = (newVal: string) => {
          setFieldValue(name, newVal);
        };

        // this prevents the console warning about switching between
        // controlled and uncontrolled input types
        let inputValue = formikValue;

        if (inputValue === undefined) {
          inputValue = null;
        }

        return (
          <WrappedRadioCardGroup
            inputProps={{ ...inputProps, onChange, value: inputValue, error: Boolean(hasError) }}
            wrapperProps={{ ...wrapperProps, ...wrapperValidation, id: name }}
          />
        );
      }}
    </Field>
  );
};
